import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import moment from "moment";
import React, { useEffect } from "react";
import CommercialDocumentNoteInput from "./CommercialDocumentNoteInput";
import { sumCentsAmountToDolar } from "utils/tools";
import InvoiceInput from "./InvoiceInput";
import { useField } from "react-final-form";

const useStyles = makeStyles({
  table: {
    marginTop: "10px",
    minWidth: 650,
    padding: "1rem",
    marginBottom: "15px",
  },
});

interface IRegularCostPreviewProps {
  resultCalculations: IResultCalculation[];
  paymentPercentages: IPaymentPercentage[];
  isCalculationButtonClicked: boolean;
}

interface InitializeCommercialDocumentsProps {
  result: IResultCalculation;
  index: number;
}

const RegularCostPreview = ({
  resultCalculations,
  paymentPercentages,
  isCalculationButtonClicked,
}: IRegularCostPreviewProps) => {
  const classes = useStyles();
  const format = "DD/MM/YY";
  const isApplyStudentSubsidyField = useField("isApplyStudentSubsidy");

  const InitializeCommercialDocuments = ({
    result,
    index,
  }: InitializeCommercialDocumentsProps) => {
    const commercialDocumentTagInvoiceMbcRef = useField(
      `commercialDocumentTags[${index}].invoiceMbcRef`,
    );
    const commercialDocumentTagRateChartId = useField(
      `commercialDocumentTags[${index}].rateChartId`,
    );

    const commercialDocumentNoteField = useField(
      `commercialDocumentNotes[${index}].note`,
    );
    const commercialDocumentNoteRateChartField = useField(
      `commercialDocumentNotes[${index}].rateChartId`,
    );

    useEffect(() => {
      if (isCalculationButtonClicked) {
        commercialDocumentTagInvoiceMbcRef.input.onChange(null);
        commercialDocumentTagRateChartId.input.onChange(result.rateChart.id);

        commercialDocumentNoteField.input.onChange(null);
        commercialDocumentNoteRateChartField.input.onChange(
          result.rateChart.id,
        );
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCalculationButtonClicked]);

    return <></>;
  };

  return (
    <div style={{ marginTop: -20 }}>
      <Grid item xs={12}>
        <Typography variant="h5">Regular Cost Preview</Typography>
      </Grid>
      {resultCalculations.map(
        (resultCalculation: IResultCalculation, index: number) => (
          <div key={`result-regular-${index}`}>
            <InitializeCommercialDocuments
              result={resultCalculation}
              index={index}
            />

            <Grid item xs={12}>
              <Typography variant="h6">
                Cost Preview for Rate Chart #{resultCalculation.rateChart.id} -{" "}
                {resultCalculation.rateChart.name}
              </Typography>

              {resultCalculation.transactions.length > 0 ? (
                <>
                  <TableContainer>
                    <Table className={classes.table} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Schedule Date</TableCell>
                          <TableCell>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resultCalculation.transactions
                          .filter(
                            (item: IResultCalculationItem) =>
                              item.type === "charge" || item.type === "refund",
                          )
                          .map((item: IResultCalculationItem, key: number) => {
                            return (
                              <TableRow key={`transaction-regular-${key}`}>
                                <TableCell>
                                  {moment.utc(item.fromDate).format(format)} -{" "}
                                  {moment.utc(item.toDate).format(format)}
                                </TableCell>
                                <TableCell>
                                  $ {(item.total / 100).toFixed(2)}
                                </TableCell>
                              </TableRow>
                            );
                          })}

                        {isApplyStudentSubsidyField.input.value && (
                          <>
                            {/* Sub Total (charge + refund) */}
                            <TableRow>
                              <TableCell>Sub Total :</TableCell>
                              <TableCell>
                                ${" "}
                                {sumCentsAmountToDolar(
                                  resultCalculation.transactions.filter(
                                    (item) =>
                                      item.type === "charge" ||
                                      item.type === "refund",
                                  ),
                                )}
                              </TableCell>
                            </TableRow>

                            {/* Subsidy or Subsidy Refund */}
                            {resultCalculation.transactions.some((item) => item.type === "subsidy") ? (
                              <TableRow>
                                <TableCell>Subsidy :</TableCell>
                                <TableCell>
                                  ${" "}
                                  {sumCentsAmountToDolar(
                                    resultCalculation.transactions.filter(
                                      (item) => item.type === "subsidy"
                                    )
                                  )}
                                </TableCell>
                              </TableRow>
                            ) : resultCalculation.transactions.some((item) => item.type === "subsidy_refund") ? (
                              <TableRow>
                                <TableCell>Subsidy Refund :</TableCell>
                                <TableCell>
                                  ${" "}
                                  {sumCentsAmountToDolar(
                                    resultCalculation.transactions.filter(
                                      (item) => item.type === "subsidy_refund"
                                    )
                                  )}
                                </TableCell>
                              </TableRow>
                            ) : null}
                          </>
                        )}

                        {/* Grand Total */}
                        <TableRow>
                          <TableCell>Total :</TableCell>
                          <TableCell>
                            ${" "}
                            {sumCentsAmountToDolar(
                              resultCalculation.transactions,
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Grid item xs={4}>
                    <Typography variant="h6" gutterBottom>
                      Document Note
                    </Typography>
                    <CommercialDocumentNoteInput
                      label="Note"
                      source={`commercialDocumentNotes[${index}].note`}
                      rateChartSource={`commercialDocumentNotes[${index}].rateChartId`}
                      rateChartId={resultCalculation.rateChart.id}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <Typography variant="h6">No Change in Cost.</Typography>
                </Grid>
              )}
            </Grid>

            {Number(sumCentsAmountToDolar(resultCalculation.transactions)) <
              0 &&
              paymentPercentages.length === 1 && (
                <div style={{ marginTop: 15 }}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Tag Invoice
                    </Typography>
                  </Grid>

                  <Grid item xs={4}>
                    <InvoiceInput
                      label="Select Invoice"
                      source={`commercialDocumentTags[${index}].invoiceMbcRef`}
                      rateChartSource={`commercialDocumentTags[${index}].rateChartId`}
                      rateChartId={resultCalculation.rateChart.id}
                      creditNoteAmount={Math.abs(
                        Number(
                          sumCentsAmountToDolar(resultCalculation.transactions),
                        ),
                      )}
                    />
                  </Grid>
                </div>
              )}
          </div>
        ),
      )}
    </div>
  );
};

export default RegularCostPreview;
